"use client"

import { marketBaseOf } from "@local/i18n/src/languages"

const UPSELL_CHANNEL_KEY_PREFIX = "up_channel_"

/**
 * localStorage key holding the upsell id selected for a market + channel.
 *
 * Keyed on the BASE market, never the public slug. Upsell config is not
 * language-aware, so neither is this key: the same rule `CartState`'s ctor
 * applies to `cart_v2_*` for the same reason (DEV-9850). `useUpsell` writes it
 * during checkout and `getUpsellUrl` reads it at payment success, and the
 * proxy's first-visit Accept-Language redirect can move the buyer across a
 * language-variant boundary in between; a slug-keyed name would hand writer and
 * reader two different keys, so the reader finds nothing and the buyer is sent
 * to /order/confirm instead of the upsell.
 *
 * No legacy-key migration exists, deliberately: `marketBaseOf` is identity for
 * every launched market, and `de` (the only market with a registered variant)
 * is not launched, so no real buyer holds a key whose name this changed.
 */
export const upsellStorageKey = (market: string, channel: string): string =>
  `up_${marketBaseOf(market)}_${channel}`

const upsellIdFromPath = (path: string): string | null => {
  const match = path.match(/\/up\/([^/?#]+)/)
  if (!match) {
    return null
  }
  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}

const resolveUpsellId = (upsellIdOrUrl: string): string | null => {
  if (upsellIdOrUrl.includes("/up/")) {
    return upsellIdFromPath(upsellIdOrUrl)
  }
  // Looks like a path but lacks /up/<id>. Refuse it so non-upsell
  // redirects (e.g. /order/confirm) don't pollute localStorage.
  if (upsellIdOrUrl.startsWith("/") || upsellIdOrUrl.startsWith("http")) {
    return null
  }
  return upsellIdOrUrl
}

export const persistUpsellChannel = (
  upsellIdOrUrl: string,
  channel: string
) => {
  if (typeof window === "undefined") {
    return
  }
  const upsellId = resolveUpsellId(upsellIdOrUrl)
  if (!upsellId || !channel) {
    return
  }
  try {
    window.localStorage.setItem(
      `${UPSELL_CHANNEL_KEY_PREFIX}${upsellId}`,
      channel
    )
  } catch {
    // localStorage may be unavailable, full, or blocked. Fail silently.
  }
}

export const readUpsellChannel = (upsellId: string): string | null => {
  if (typeof window === "undefined") {
    return null
  }
  try {
    return window.localStorage.getItem(
      `${UPSELL_CHANNEL_KEY_PREFIX}${upsellId}`
    )
  } catch {
    return null
  }
}
